﻿import { Intent } from '@blueprintjs/core';
import { ButtonSave, Button } from 'components/buttons';
import { FormTextArea, FormDateInput } from "components/form-fields";
import { Modal, NotificationToaster, NotificationInline, ModalSize } from 'components/notifications';
import React, { useEffect, useState, Fragment, useRef } from 'react';
import { JobService } from 'services';
import { UserAccess } from 'components/user-access/UserAccess';
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from 'pages/errors/page-restricted';
import { useDirtyData } from "hooks/useDirtyData";
import PropTypes from 'prop-types';
import moment from "moment";
import { ShowHide } from "components/layout";

const defaultFailAppointmentReason = {
    failureReason: "",
    failureAdditionalComments: "",
    completedDate: moment().format("YYYY-MM-DD")
};

export function FailVisit(
    {
        jobFailed = false,
        failAppointmentReason = defaultFailAppointmentReason,
        addressSettings = null,
        loading = false,
        onFailVisitClose = null,
        onFailVisitSave = null,
        showFailedAppointment = false,
        isFailed = false,
        jobId
    }
) {

    const [failAppointmentReasonValue, setFailAppointmentReason] = useState(defaultFailAppointmentReason);
    const [originalFailAppointmentValue, setOriginalFailAppointmentValue] = useState(defaultFailAppointmentReason);
    const [isFailAppointmentValid, failAppointmentErrors, failAppointmentsValidate] = useValidation();
    const [showFailJobModal, setShowFailJobModal] = useState(false);
    const [showCreateCaseFailed, setShowCreateCaseFailed] = useState(false);
    const [savingFailedAppointment, setSavingFailedAppointment] = useState(false);
    const isDataDirty = useDirtyData(originalFailAppointmentValue, failAppointmentReasonValue);
    const requiredAction = ["Internal"];
    const failVisitReasonRef = useRef(null);
    const rules = [
        { fieldName: "failureReason", required: true }
    ];

    useEffect(() => {
        if (failAppointmentReason !== null && failAppointmentReason !== undefined) {
            setOriginalFailAppointmentValue(failAppointmentReason);
            setFailAppointmentReason(failAppointmentReason);
        }
    }, [failAppointmentReason]);

    useEffect(() => {
        failAppointmentsValidate(rules, failAppointmentReasonValue);
    }, [failAppointmentReasonValue]);

    useEffect(() => {
        setShowFailJobModal(showFailedAppointment);
        if (showFailedAppointment) {
            setTimeout(function () {
                if (failVisitReasonRef.current != null) {
                    failVisitReasonRef.current.focus();
                    failVisitReasonRef.current.selectionStart = failVisitReasonRef.current.selectionEnd - 1;
                }
            });
        }
    }, [showFailedAppointment]);

    function onFailAppointmentReasonChange(event) {
        setFailAppointmentReason({
            ...failAppointmentReasonValue,
            failureReason: event.target.value
        });
    }
    function onFailAppointmentCommentChange(event) {
        setFailAppointmentReason({
            ...failAppointmentReasonValue,
            failureAdditionalComments: event.target.value
        });
    }

    function onFailVisitSaveValue(failAppointmentReasonToReturn) {
        if (onFailVisitSave !== null) {
            onFailVisitSave(failAppointmentReasonToReturn);
        };
    }

    function onFailVisitCloseValue(isCancel, caseFailed) {
        if (onFailVisitClose !== null) {
            onFailVisitClose(!caseFailed);
        };
        if (isCancel) {
            setFailAppointmentReason(originalFailAppointmentValue);
        }
        setShowFailJobModal(caseFailed);
    }

    function onFailedDateChange(item) {
        setFailAppointmentReason({
            ...failAppointmentReasonValue,
            completedDate: item ? moment(item).format("YYYY-MM-DD") : null
        });
    }

    function onCaseCreateFailedClose() {
        setOriginalFailAppointmentValue(failAppointmentReasonValue);
        onFailVisitSaveValue(failAppointmentReasonValue);
        setShowCreateCaseFailed(false);
        onFailVisitCloseValue(true);
    }

    function onFailJobConfirm() {
        if (!isFailAppointmentValid) {
            return;
        }

        var failedCase = false;
        setSavingFailedAppointment(true);
        JobService.failAppointment(jobId, failAppointmentReasonValue).then(function () {
            setOriginalFailAppointmentValue(failAppointmentReasonValue);
            NotificationToaster.show(Intent.SUCCESS, `The Appointment has been ${isFailed ? "failed" : "saved"} successfully.`);
            onFailVisitSaveValue(failAppointmentReasonValue);
        }, function (error) {
            if (error.indexOf("Failed Appointment OK. Dynamic Case Failed.") !== -1) {
                setShowCreateCaseFailed(true);
                failedCase = true;
            }
            else {
                NotificationToaster.show(Intent.DANGER, error);
            }

        }).finally(function () {
            setSavingFailedAppointment(false);
            onFailVisitCloseValue(false, failedCase);
        });
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!showCreateCaseFailed}
                        show={(
                            <Modal
                                isOpen={showFailJobModal}
                                title="Are you sure you want to fail this appointment?"
                                showCloseButton={false}
                                clickOutsideClose={false}
                                size={ModalSize.MEDIUM}
                            >
                                <div>
                                    <NotificationInline
                                        allowClose={false}
                                        show
                                        text="Please note, once you fail this appointment, the action cannot be reversed."
                                        intent={Intent.WARNING}
                                    />
                                    <NotificationInline
                                        allowClose={false}
                                        show={showCreateCaseFailed}
                                        text="The failed appointment was saved. The dynamics case was not created. Please contact IT support."
                                        intent={Intent.WARNING}
                                    />
                                    <FormDateInput
                                        id="fail-appointment-date"
                                        headingText="Failed Date:"
                                        onChange={onFailedDateChange}
                                        value={failAppointmentReasonValue.completedDate}
                                        minDate={moment("1900-01-01").toDate()}
                                        maxDate={moment().toDate()}
                                        dangerHelperText={failAppointmentErrors.completedDate}
                                    />
                                    <FormTextArea
                                        headingText="Reason for failing appointment (customer viewable):"
                                        value={failAppointmentReasonValue.failureReason}
                                        onChange={onFailAppointmentReasonChange}
                                        dangerHelperText={failAppointmentErrors.failureReason}
                                        id="input-fail-appointment-reason"
                                        inputRef={failVisitReasonRef}
                                    />
                                    <FormTextArea
                                        headingText="Additional comments (internal viewable):"
                                        value={failAppointmentReasonValue.failureAdditionalComments}
                                        onChange={onFailAppointmentCommentChange}
                                        id="input-fail-appointment-comments"
                                    />
                                </div>
                                <div className="bp6-dialog-footer">
                                    <div className="bp6-dialog-footer-actions">
                                        <ButtonSave text={(jobFailed ? "Save" : "Fail appointment")} icon={null} intent={(jobFailed ? Intent.PRIMARY : Intent.DANGER)} onClick={onFailJobConfirm} simpleDisabled={!isFailAppointmentValid && isDataDirty} disabled={savingFailedAppointment}></ButtonSave>
                                        <Button text="Cancel" onClick={() => { onFailVisitCloseValue(true); }} disabled={savingFailedAppointment}></Button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                    />
                    <ShowHide
                        evaluator={showCreateCaseFailed}
                        show={(
                                <Modal
                                    isOpen={showFailJobModal}
                                    title="Failed Appointment warning."
                                    showCloseButton={false}
                                    clickOutsideClose={false}
                                >
                                    <NotificationInline
                                        allowClose={false}
                                        show={showCreateCaseFailed}
                                    text="The failed appointment was saved. However the dynamics case could not be created. Please create the case in dynamics, and contact IT support to see why the case could not be created."
                                        intent={Intent.WARNING}
                                    />
                                    <Button text="Close" onClick={() => { onCaseCreateFailedClose(); }}></Button>
                                </Modal>
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

FailVisit.propTypes = {
    jobFailed: PropTypes.bool,
    failAppointmentReason: PropTypes.object,
    jobId: PropTypes.number,
    loading: PropTypes.bool.isRequired,
    onFailVisitClose: PropTypes.func,
    onFailVisitSave: PropTypes.func,
    showFailedAppointment: PropTypes.bool.isRequired,
    isFailed: PropTypes.bool.isRequired
};